import { DataTableRow } from '@/components/molecules/DataTableRow'
import { ListSearchInput, WorkshopSelect } from '@/components/molecules/ListFilters'
import { ListPagination } from '@/components/molecules/ListPagination'
import { SortableTableHead } from '@/components/molecules/SortableTableHead'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useListParams } from '@/hooks/useListParams'
import { formatDate } from '@/lib/utils'
import { fetchInvoices } from '@/services/invoices'
import type { InvoiceListQuery, WorkshopLocation } from '@backend/schemas'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

type SortField = InvoiceListQuery['sortField']

export const InvoicesTable = () => {
  const navigate = useNavigate()
  const { params, update, setSort } = useListParams<SortField>({ sortField: 'invoiceDate', sortDir: 'desc' })
  const query = { ...params, workshop: (params.workshop || undefined) as WorkshopLocation | undefined }

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['invoices', query],
    queryFn: () => fetchInvoices(query),
    placeholderData: keepPreviousData,
  })

  const sortProps = {
    sortField: params.sortField,
    sortDir: params.sortDir,
    onSort: (field: SortField) => setSort(field, field === 'invoiceDate' ? 'desc' : 'asc'),
    disabled: !!params.search,
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fakturor</CardTitle>
        <div className="flex gap-4 mt-4">
          <ListSearchInput
            placeholder="Sök fakturanummer (börjar med)..."
            value={params.search}
            onChange={search => update({ search })}
          />
          <WorkshopSelect value={params.workshop} onChange={workshop => update({ workshop })} />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead label="Fakturanummer" field="invoiceNumber" {...sortProps} />
                  <SortableTableHead label="Leverantör" field="vendor" {...sortProps} />
                  <SortableTableHead label="Verkstad" field="workshop" {...sortProps} />
                  <TableHead>Org.nr</TableHead>
                  <SortableTableHead label="Datum" field="invoiceDate" {...sortProps} />
                  <TableHead>Antal bilar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices?.items.length ? (
                  invoices.items.map(invoice => (
                    <DataTableRow key={invoice.id} onClick={() => navigate(`/invoices/${invoice.id}`)}>
                      <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                      <TableCell>{invoice.vendor || '-'}</TableCell>
                      <TableCell className="capitalize">{invoice.workshop || '-'}</TableCell>
                      <TableCell>{invoice.vendorOrganizationNumber || '-'}</TableCell>
                      <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                      <TableCell>{invoice.cars.length}</TableCell>
                    </DataTableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      {params.search || params.workshop ? 'Inga fakturor matchade sökningen' : 'Inga fakturor hittades'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <ListPagination
              page={params.page}
              pageSize={invoices?.pageSize ?? 100}
              total={invoices?.total ?? 0}
              itemLabel="fakturor"
              onPageChange={page => update({ page: String(page) })}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
